import { useEffect } from "react";

/**
 * H12 · Soft-keyboard handling.
 *
 * `body { overflow: hidden }` (main.css) means there is no document scroll, and
 * the app shell sizes its scrollers off `100dvh` / `calc(100vh - …)`. Neither
 * `vh` nor `dvh` shrinks when a software keyboard opens — the keyboard covers
 * the layout viewport, it does not resize it — so on a phone the keyboard
 * simply paints over the bottom ~45% of the app. The audit found the AI
 * Assistant composer disappearing behind it with no scroller able to bring it
 * back, and the code comment there claimed the case was handled. It was not:
 * the repo had zero `visualViewport` listeners and no `interactive-widget=`
 * declaration anywhere.
 *
 * `window.visualViewport` is the only surface that reports the keyboard. This
 * hook mirrors it onto two custom properties on `<html>`:
 *
 *   --vvh          the visual viewport height in px — a `dvh` that actually moves
 *   --keyboard-inset  how much of the layout viewport the keyboard covers
 *
 * Anything that must stay above the keyboard (a pinned composer, a sheet's
 * footer) pads itself by `var(--keyboard-inset)`. Anything sized to the screen
 * uses `var(--vvh)` in place of `100dvh`.
 *
 * Called once from the app shell. Safe where `visualViewport` is absent (older
 * Safari, jsdom): the properties keep their static fallbacks and nothing moves.
 */
export function useVisualViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;

    const apply = () => {
      const height = vv?.height ?? window.innerHeight;
      // `offsetTop` covers the case where the page is scrolled under the
      // keyboard rather than resized — iOS does both depending on version.
      const covered = Math.max(
        0,
        window.innerHeight - height - (vv?.offsetTop ?? 0)
      );
      root.style.setProperty("--vvh", `${height}px`);
      root.style.setProperty("--keyboard-inset", `${Math.round(covered)}px`);
      root.classList.toggle("keyboard-open", covered > 120);
    };

    apply();
    if (!vv) {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, []);
}

export default useVisualViewport;
